import React, { useEffect, useMemo, useState } from "react";
import { VENDOR_CATEGORIES } from "../../constants/vendorCategories";
import "./ResidentVendors.css";

export default function ResidentVendors() {
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    category: "",
    serviceName: "",
    vendorName: "",
    contactPerson: "",
    phone: "",
    referredBy: "",
    referredByPhone: "",
    remarks: "",
  });

  const API = process.env.REACT_APP_API_URL;
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("residentToken")}` };

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/vendors`, { headers: authHeader });
      if (!res.ok) {
        setError("Failed to load vendor directory");
        return;
      }
      setEntries(await res.json());
    } catch (err) {
      setError("Server error while loading directory");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchesCategory = activeCategory === "All" || e.category === activeCategory;
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        e.serviceName?.toLowerCase().includes(q) ||
        e.vendorName?.toLowerCase().includes(q) ||
        e.contactPerson?.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [entries, activeCategory, search]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.category || !form.serviceName || !form.phone) {
      setError("Category, service name, and phone are required");
      return;
    }

    try {
      const res = await fetch(`${API}/api/vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to add entry");
      }

      setForm({
        category: "", serviceName: "", vendorName: "", contactPerson: "",
        phone: "", referredBy: "", referredByPhone: "", remarks: "",
      });
      setSuccess("Added to the directory");
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="resident-layout">
      
      <div className="resident-content">
        <div className="vendors-header-row">
          <h1 className="vendors-title">Vendor Directory</h1>
          <button className="vendors-add-btn" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Cancel" : "+ Contribute a Vendor"}
          </button>
        </div>
        <p className="vendors-sub">Recommended vendors and service contacts shared by residents.</p>

        {error && <p className="vendors-error">{error}</p>}
        {success && <p className="vendors-success">{success}</p>}

        {showForm && (
          <form onSubmit={handleSubmit} className="vendors-form">
            <div className="vendors-form-row">
              <label className="vendors-label">
                Category
                <select name="category" className="vendors-input" value={form.category} onChange={handleChange}>
                  <option value="">Select category</option>
                  {VENDOR_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="vendors-label">
                Service / Product
                <input name="serviceName" className="vendors-input" value={form.serviceName} onChange={handleChange} placeholder="e.g. AC Technician" />
              </label>
            </div>

            <div className="vendors-form-row">
              <label className="vendors-label">
                Vendor / Company Name
                <input name="vendorName" className="vendors-input" value={form.vendorName} onChange={handleChange} />
              </label>
              <label className="vendors-label">
                Contact Person
                <input name="contactPerson" className="vendors-input" value={form.contactPerson} onChange={handleChange} />
              </label>
            </div>

            <div className="vendors-form-row">
              <label className="vendors-label">
                Phone
                <input name="phone" className="vendors-input" value={form.phone} onChange={handleChange} />
              </label>
              <label className="vendors-label">
                Your Name (referred by)
                <input name="referredBy" className="vendors-input" value={form.referredBy} onChange={handleChange} />
              </label>
            </div>

            <label className="vendors-label">
              Remarks (optional)
              <input name="remarks" className="vendors-input" value={form.remarks} onChange={handleChange} />
            </label>

            <button type="submit" className="vendors-submit">Add to Directory</button>
          </form>
        )}

        <div className="vendors-filters">
          <input
            className="vendors-search"
            placeholder="Search by service, vendor, or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="vendors-tabs">
            <button
              className={activeCategory === "All" ? "vendors-tab active" : "vendors-tab"}
              onClick={() => setActiveCategory("All")}
            >
              All
            </button>
            {VENDOR_CATEGORIES.map((c) => (
              <button
                key={c}
                className={activeCategory === c ? "vendors-tab active" : "vendors-tab"}
                onClick={() => setActiveCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="vendors-list">
          {filtered.map((e) => (
            <div className="vendors-item" key={e.id}>
              <div className="vendors-item-header">
                <span className="vendors-item-service">{e.serviceName}</span>
                <span className="vendors-item-category">{e.category}</span>
              </div>
              {e.vendorName && <div className="vendors-item-vendor">{e.vendorName}</div>}
              <div className="vendors-item-contact">
                {e.contactPerson ? `${e.contactPerson} — ` : ""}{e.phone}
              </div>
              {e.referredBy && (
                <div className="vendors-item-referred">Referred by {e.referredBy}{e.referredByPhone ? ` (${e.referredByPhone})` : ""}</div>
              )}
              {e.remarks && <div className="vendors-item-remarks">{e.remarks}</div>}
            </div>
          ))}

          {filtered.length === 0 && !error && (
            <p className="vendors-empty">No entries found.</p>
          )}
        </div>
      </div>
    </div>
  );
}